import React, { useState } from 'react';
import { Book, Bookmark } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import styles from './Css/BookDisplay.module.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { useNavigate } from 'react-router-dom';
import { useCreatePlaylist, useCheckContentInPlaylist } from '../../../hooks/usePlayListHooks';
import { useEbooksByLanguage } from '../../../hooks/ebooksByLanguageHooks';

const BookDisplay = ({ languageName }) => {
  // Use the useEbooksByLanguage hook with the specified parameters
  const { 
    ebooks, 
    isLoading, 
    error 
  } = useEbooksByLanguage(languageName, {
    page: 1,
    limit: 5,
    sort: 'latest',
    uniqueSeries: true
  });
  
  const navigate = useNavigate();
  
  // Create playlist mutation
  const { mutate: createPlaylist, isLoading: creatingPlaylist } = useCreatePlaylist();

  // State to track saved books (i.e., playlist) across the component
  const [savedBooks, setSavedBooks] = useState(() => {
    // Initialize saved books from localStorage or empty array
    const savedBooksFromStorage = localStorage.getItem('savedBooks');
    return savedBooksFromStorage ? JSON.parse(savedBooksFromStorage) : [];
  });

  // Function to check if a book is already in a playlist
  const useBookPlaylistCheck = (bookId) => {
    const { 
      data: playlistCheck, 
      isLoading: checkingPlaylist,
      refetch: refetchPlaylistCheck 
    } = useCheckContentInPlaylist(bookId ? "ebook" : null, bookId);

    return { playlistCheck, checkingPlaylist, refetchPlaylistCheck };
  };

  // Featured Book Component
  const FeaturedBook = ({ book }) => {
    // Check playlist status for this specific book
    const { playlistCheck, checkingPlaylist, refetchPlaylistCheck } = useBookPlaylistCheck(book.id);
    
    // Check if the book is already saved
    const isBookmarked = savedBooks.some(savedBook => savedBook.id === book.id);
    
    // Toggle bookmark function
    const toggleBookmark = (e) => {
      e.stopPropagation(); // Prevent event from bubbling up

      // If book is already in playlist, do nothing
      if (playlistCheck?.inPlaylist) return;

      // Create playlist for the book
      createPlaylist({
        item_type: "ebook",
        content_id: book.id,
        title: `${book.title} - Library`,
        description: `Library entry for ${book.title}`
      }, {
        onSuccess: () => {
          // Update saved books state
          setSavedBooks(prevSavedBooks => {
            const updatedBooks = isBookmarked
              ? prevSavedBooks.filter(savedBook => savedBook.id !== book.id)
              : [...prevSavedBooks, book];
            
            return updatedBooks;
          });

          // Refetch playlist check to update status
          refetchPlaylistCheck();
        }
      });
    };

    const handleImageClick = (e) => {
      // Prevent event bubbling from the image click
      e.stopPropagation();

      // Navigate to the book detail page
      navigate(`/book-detail/${book.slug}`);
    };

    return (
      <div className={styles.featuredBook}>
        <img
          src={book.cover_image_url_for_mbl}
          alt={book.title}
          className={styles.featuredImage}
          onClick={handleImageClick}
        />
        <div 
          className={`${styles.bookmarkIcon} ${playlistCheck?.inPlaylist ? styles.bookmarked : ''}`} 
          onClick={toggleBookmark}
        >
          <Bookmark 
            size={20} 
            fill={playlistCheck?.inPlaylist ? "#e74d3c" : "none"} 
            color={playlistCheck?.inPlaylist ? "#e74d3c" : "currentColor"}
          />
        </div>
        <div className={styles.featuredContent}>
          <h2 className={styles.bookTitle}>{book.title}</h2>
          <p className={styles.bookInfo}>
            {book.totalPages ? `${book.totalPages} pages` : ''} 
            {book.totalPages && book.genre_name ? ' | ' : ''} 
            {book.genre_name || ''}
          </p>
        </div>
      </div>
    );
  };

  // Book Card Component
  const BookCard = ({ book }) => {
    // Check playlist status for this specific book
    const { playlistCheck, checkingPlaylist, refetchPlaylistCheck } = useBookPlaylistCheck(book.id);
    
    // Toggle bookmark function
    const toggleBookmark = (e) => {
      e.stopPropagation(); // Prevent event from bubbling up

      // If book is already in playlist, do nothing
      if (playlistCheck?.inPlaylist) return;

      // Create playlist for the book
      createPlaylist({
        item_type: "ebook",
        content_id: book.id,
        title: `${book.title} - Library`,
        description: `Library entry for ${book.title}`
      }, {
        onSuccess: () => {
          // Update saved books state
          setSavedBooks(prevSavedBooks => {
            const isBookmarked = prevSavedBooks.some(savedBook => savedBook.id === book.id);
            const updatedBooks = isBookmarked
              ? prevSavedBooks.filter(savedBook => savedBook.id !== book.id)
              : [...prevSavedBooks, book];
            
            return updatedBooks;
          });

          // Refetch playlist check to update status
          refetchPlaylistCheck();
        }
      });
    };

    return (
      <div className={styles.bookCard}>
        <img
          src={book.cover_image_url_for_mbl || '/api/placeholder/150/200'}
          alt={book.title}
          className={styles.cardImage}
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
              e.preventDefault(); // Prevent any default behavior
              navigate(`/book-detail/${book.slug}`);
            }}
        />
        <div className={styles.cardContent}>
          <h3 className={styles.cardTitle}>{book.title}</h3>
          <p className={styles.cardInfo}>
            <span className={styles.author}>By {book.creator_name}</span> | {book.genre_name}
          </p>
          <p className={styles.views}>{book.views_count} views</p>
        </div>
        <div className={styles.bookmarkIcons}>
          <button
            className={styles.bookmarkButton}
            onClick={(e) => {
              e.preventDefault(); // Prevent any default behavior
              navigate(`/book-detail/${book.slug}`);
            }}
          >
            <Book
              size={16}
              color="white"
              fill="none"
            />
          </button>
        </div>
      </div>
    );
  };

  return (
    (ebooks && ebooks.length > 0) ? (
      <div className={styles.container}>

        {ebooks && ebooks.length > 0 && (
          <div className={styles.recommendedSection}>
            <h2 className={styles.sectionTitle}>{`${languageName || 'Featured'} E-books`}</h2>
            {isLoading ? (
              <div className={styles.loadingState}>Loading ebooks...</div>
            ) : error ? (
              <div className={styles.errorState}>Failed to load ebooks</div>
            ) : (
              <Swiper
                modules={[Navigation, Pagination]}
                spaceBetween={20}
                navigation
                pagination={{ clickable: true }}
                className={styles.swiperContainer}
                breakpoints={{
                  320: {
                    slidesPerView: 1,
                    spaceBetween: 10
                  },
                  640: {
                    slidesPerView: 2,
                    spaceBetween: 15
                  },
                  1024: {
                    slidesPerView: 3,
                    spaceBetween: 20
                  },
                  1200: {
                    slidesPerView: 4,
                    spaceBetween: 20
                  }
                }}
              >
                {ebooks.map(book => (
                  <SwiperSlide key={book.id}>
                    <BookCard book={book} />
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </div>
        )}
      </div>
    ) : null
  );
};

export default BookDisplay;